import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { cn } from "@/lib/utils";
import { Z_INDEX_STEP } from "@/lib/modal-stack";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useTrackEvent } from "libs/analytics";
import { generateAltText } from "api/post";

import { getFormPost } from "selectors/postFormSelectors";
import type { PostFormRootState } from "selectors/postFormSelectors";
// @ts-ignore
import { getMedia } from "components/Posts/Post/PostMedia/PostMedia";
import { NgMediaFile, NgPost } from "features/post/format-post";
import type { PlatformSpec } from "constants/Platforms";

// Per-platform ALT TEXT limits. These are deliberately NOT the caption
// CHARACTER_LIMITS (constants/CharacterLimits) — alt text has its own, much
// tighter caps. Only platforms that support alt text appear here; the
// effective counter binds to the strictest selected platform.
const ALT_TEXT_LIMITS: Partial<Record<PlatformSpec["TYPE"], number>> = {
  IG: 1000,
  FB: 1000,
  TW: 1000,
  LI: 120,
  PI: 500,
};
const ALT_TEXT_PLATFORM_LABELS: Partial<Record<PlatformSpec["TYPE"], string>> =
  {
    IG: "Instagram",
    FB: "Facebook",
    TW: "X (Twitter)",
    LI: "LinkedIn",
    PI: "Pinterest",
  };
// Conservative fallback when no alt-text-supporting platform is resolvable,
// plus a hard ceiling that only guards against pathological pastes.
const FALLBACK_ALT_LIMIT = 500;
const HARD_ALT_CEILING = 1000;

type GenerationStatus = "idle" | "generating" | "done" | "error";
type GenerationSource = "auto" | "regenerate" | "regenerate-all" | "retry";

interface AltTextProps {
  post: NgPost;
  onClose?: () => void;
  onSubmit(value: NgMediaFile[]): void;
  toggleAddAlt(): void;
  handleImageAltUpdate(value: string | unknown): void;
  platforms?: PlatformSpec[];
}

// Stable per-image key. asset_id is the durable identity; url is the
// fallback for freshly-uploaded items that don't have one yet.
function keyOf(media: NgMediaFile): string {
  return media.asset_id || media.url;
}

// Seed the editor's local working copy from the post. getMedia reconciles the
// legacy top-level altText with per-item altText; we re-attach asset_id from
// the raw mediaFiles because getMedia can drop it.
function seedMedia(sourcePost: NgPost | undefined): NgMediaFile[] {
  if (!sourcePost) return [];
  const media = getMedia(sourcePost) as NgMediaFile[];
  const sourceMediaFiles = (sourcePost.mediaFiles || []) as NgMediaFile[];
  return media.map((item: NgMediaFile, index: number) => ({
    ...item,
    asset_id: item.asset_id || sourceMediaFiles[index]?.asset_id,
  }));
}

// Effective alt-text limit = strictest cap across the selected platforms that
// support alt text, plus the platform that sets it (for the counter tooltip).
function computeAltLimit(platforms?: PlatformSpec[]): {
  limit: number;
  platformLabel: string | null;
} {
  const capped = (platforms || [])
    .map((p) => ({ type: p.TYPE, limit: ALT_TEXT_LIMITS[p.TYPE] }))
    .filter(
      (entry): entry is { type: PlatformSpec["TYPE"]; limit: number } =>
        typeof entry.limit === "number"
    );

  if (capped.length === 0) {
    return { limit: FALLBACK_ALT_LIMIT, platformLabel: null };
  }

  const strictest = capped.reduce((min, entry) =>
    entry.limit < min.limit ? entry : min
  );
  return {
    limit: strictest.limit,
    platformLabel: ALT_TEXT_PLATFORM_LABELS[strictest.type] || null,
  };
}

// Minimal concurrency limiter. We fan out one generation request per image and
// cap in-flight calls so a big carousel doesn't hammer the API. Inlined rather
// than pulling in p-limit (ESM-only, outside vi's transform allow-list).
function createLimiter(concurrency: number) {
  let active = 0;
  const queue: Array<() => void> = [];
  const drain = () => {
    active -= 1;
    const next = queue.shift();
    if (next) next();
  };
  return function run<T>(fn: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const exec = () => {
        active += 1;
        fn().then(resolve, reject).finally(drain);
      };
      if (active < concurrency) exec();
      else queue.push(exec);
    });
  };
}

const GENERATION_CONCURRENCY = 3;
const SAVED_INDICATOR_MS = 1500;

function AltTextModalComponent({
  toggleAddAlt,
  onSubmit,
  post,
  handleImageAltUpdate,
  platforms,
}: AltTextProps) {
  const trackEvent = useTrackEvent();
  const currentPost = useSelector((state: PostFormRootState) =>
    getFormPost(state, post._id)
  );

  // Local working copy — authoritative for the inputs so typing is instant.
  // Seeded once on open (this component only mounts while the drawer is open),
  // which is what kills the old "reseed on every store change wipes my edits"
  // jank. Persistence is handled by committing each change back to the post.
  const [mediaFiles, setMediaFiles] = useState<NgMediaFile[]>(() =>
    // currentPost is typed as Post | NgPost; both carry mediaFiles and post is
    // always defined, so treat the resolved source as an NgPost for seeding.
    seedMedia((currentPost || post) as NgPost)
  );
  const mediaFilesRef = useRef(mediaFiles);
  useEffect(() => {
    mediaFilesRef.current = mediaFiles;
  }, [mediaFiles]);

  const [keywords, setKeywords] = useState("");
  const [statusByKey, setStatusByKey] = useState<
    Record<string, GenerationStatus>
  >({});
  const [errorKeys, setErrorKeys] = useState<Record<string, boolean>>({});
  const [justSaved, setJustSaved] = useState(false);
  const [liveMessage, setLiveMessage] = useState("");
  // Errors get their own assertive region so a screen reader interrupts rather
  // than queueing the failure behind polite progress announcements.
  const [errorMessage, setErrorMessage] = useState("");
  const [confirmRegenAll, setConfirmRegenAll] = useState(false);

  // Keys the user has hand-edited. A ref (not state) because it must be read
  // synchronously inside async generation callbacks to decide whether a late
  // result may overwrite, and to gate the regenerate-all confirm.
  const userEditedKeys = useRef<Set<string>>(new Set());
  const abortRegistry = useRef<Map<string, AbortController>>(new Map());
  const hasAutoRun = useRef(false);
  const isMountedRef = useRef(true);
  const limitRef = useRef(createLimiter(GENERATION_CONCURRENCY));
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const imageMediaFiles = useMemo(
    () => mediaFiles.filter((item) => !item.isVideo),
    [mediaFiles]
  );
  const altLimit = useMemo(() => computeAltLimit(platforms), [platforms]);

  const isAnyGenerating = useMemo(
    () => Object.values(statusByKey).some((s) => s === "generating"),
    [statusByKey]
  );

  useEffect(() => {
    // Reset on (re)mount so StrictMode's dev-only mount→unmount→remount cycle
    // doesn't leave the flag stuck false and suppress generation.
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortRegistry.current.forEach((controller) => controller.abort());
      abortRegistry.current.clear();
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
  }, []);

  const setStatus = useCallback((key: string, status: GenerationStatus) => {
    setStatusByKey((prev) => ({ ...prev, [key]: status }));
  }, []);

  const setErrored = useCallback((key: string, value: boolean) => {
    setErrorKeys((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Persist the working copy to the post form. Live autosave: this fires on
  // every edit and every generated result, so closing the drawer any way keeps
  // everything. Single-image posts also write the legacy top-level altText so
  // getMedia's single-file reconciliation stays in sync.
  const commit = useCallback(
    (next: NgMediaFile[]) => {
      onSubmit(next);
      if (next.length === 1) {
        handleImageAltUpdate(next[0].altText);
      }
      setJustSaved(true);
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => {
        if (isMountedRef.current) setJustSaved(false);
      }, SAVED_INDICATOR_MS);
    },
    [onSubmit, handleImageAltUpdate]
  );

  // Write alt text for one image into the working copy and persist it.
  const applyAlt = useCallback(
    (key: string, value: string) => {
      const next = mediaFilesRef.current.map((item) =>
        keyOf(item) === key ? { ...item, altText: value } : item
      );
      mediaFilesRef.current = next;
      setMediaFiles(next);
      commit(next);
    },
    [commit]
  );

  const generateFor = useCallback(
    async (media: NgMediaFile, source: GenerationSource) => {
      if (!isMountedRef.current) return;

      const key = keyOf(media);
      const position = imageMediaFiles.findIndex((m) => keyOf(m) === key);
      const label = position >= 0 ? `image ${position + 1}` : "this image";

      abortRegistry.current.get(key)?.abort();
      const controller = new AbortController();
      abortRegistry.current.set(key, controller);

      setStatus(key, "generating");
      setErrored(key, false);
      setErrorMessage("");
      setLiveMessage(`Generating alt text for ${label}.`);

      try {
        const mediaUrl = media.originalUrl || media.url;
        if (!media.asset_id && !mediaUrl) {
          throw new Error("Missing media reference");
        }

        const basePayload = media.asset_id
          ? { assetId: media.asset_id }
          : { mediaUrl };
        const trimmedKeywords = keywords.trim();
        const payload = trimmedKeywords
          ? { ...basePayload, keywords: trimmedKeywords }
          : basePayload;

        let response: { altText: string };
        try {
          response = await generateAltText(payload, {
            signal: controller.signal,
          });
        } catch (error) {
          // Deploy-order safety: an older sked-api hasn't shipped the `keywords`
          // field yet and its $$strict validator rejects it with a 400. Fall
          // back to a keyword-less generation so the user still gets alt text
          // regardless of which app deploys first.
          const status = (error as { response?: { status?: number } })?.response
            ?.status;
          if (
            trimmedKeywords &&
            status === 400 &&
            !controller.signal.aborted &&
            isMountedRef.current
          ) {
            response = await generateAltText(basePayload, {
              signal: controller.signal,
            });
          } else {
            throw error;
          }
        }

        if (controller.signal.aborted || !isMountedRef.current) return;

        // Never clobber text the user typed while we were generating.
        if (!userEditedKeys.current.has(key)) {
          applyAlt(key, response.altText);
          setStatus(key, "done");
          setLiveMessage(`Alt text ready for ${label}.`);
        } else {
          setStatus(key, "idle");
        }

        trackEvent({
          eventName: "create-post-alt-text-generation",
          metadata: { success: true, source },
          useServices: ["AMP"],
        });
      } catch {
        if (controller.signal.aborted || !isMountedRef.current) return;

        setStatus(key, "error");
        setErrored(key, true);
        setErrorMessage(
          `Couldn't generate alt text for ${label}. Use the retry button.`
        );
        trackEvent({
          eventName: "create-post-alt-text-generation",
          metadata: { success: false, source },
          useServices: ["AMP"],
        });
      } finally {
        if (abortRegistry.current.get(key) === controller) {
          abortRegistry.current.delete(key);
        }
      }
    },
    [applyAlt, imageMediaFiles, keywords, setErrored, setStatus, trackEvent]
  );

  // Each generation snapshots `keywords` at enqueue time (via the generateFor
  // closure). Editing the keywords field mid-flight intentionally does NOT
  // change an in-flight request — it applies to the next generate/regenerate.
  const enqueue = useCallback(
    (media: NgMediaFile, source: GenerationSource) => {
      void limitRef.current(() => generateFor(media, source));
    },
    [generateFor]
  );

  // Generate on open — empty rows only. Runs once; already-filled images and
  // hand-edited rows are left untouched.
  useEffect(() => {
    if (hasAutoRun.current) return;
    if (imageMediaFiles.length === 0) return;
    hasAutoRun.current = true;
    imageMediaFiles
      .filter((media) => !media.altText)
      .forEach((media) => enqueue(media, "auto"));
  }, [enqueue, imageMediaFiles]);

  const handleUserEdit = useCallback(
    (media: NgMediaFile, value: string) => {
      const key = keyOf(media);
      userEditedKeys.current.add(key);
      abortRegistry.current.get(key)?.abort();
      setStatus(key, "idle");
      setErrored(key, false);
      applyAlt(key, value);
    },
    [applyAlt, setErrored, setStatus]
  );

  const handleRegenerateOne = useCallback(
    (media: NgMediaFile) => {
      const key = keyOf(media);
      userEditedKeys.current.delete(key);
      enqueue(media, statusByKey[key] === "error" ? "retry" : "regenerate");
    },
    [enqueue, statusByKey]
  );

  const doRegenerateAll = useCallback(() => {
    imageMediaFiles.forEach((media) => {
      userEditedKeys.current.delete(keyOf(media));
      enqueue(media, "regenerate-all");
    });
  }, [enqueue, imageMediaFiles]);

  const handleRegenerateAll = useCallback(() => {
    const anyEdited = imageMediaFiles.some((media) =>
      userEditedKeys.current.has(keyOf(media))
    );
    if (anyEdited) {
      setConfirmRegenAll(true);
      return;
    }
    doRegenerateAll();
  }, [doRegenerateAll, imageMediaFiles]);

  const handleClearAll = useCallback(() => {
    abortRegistry.current.forEach((controller) => controller.abort());
    abortRegistry.current.clear();
    const next = mediaFilesRef.current.map((item) =>
      item.isVideo ? item : { ...item, altText: "" }
    );
    imageMediaFiles.forEach((media) =>
      userEditedKeys.current.add(keyOf(media))
    );
    mediaFilesRef.current = next;
    setMediaFiles(next);
    setStatusByKey({});
    setErrorKeys({});
    commit(next);
  }, [commit, imageMediaFiles]);

  const requestClose = useCallback(() => {
    // Everything is already persisted (live autosave), so closing is always
    // safe — just stop any in-flight work and unmount.
    abortRegistry.current.forEach((controller) => controller.abort());
    abortRegistry.current.clear();
    toggleAddAlt();
  }, [toggleAddAlt]);

  const hasImages = imageMediaFiles.length > 0;

  return (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open) requestClose();
      }}
    >
      <SheetContent
        side="right"
        openDuration="fast"
        closedDuration="fast"
        showCloseButton={false}
        // The drawer opens from inside the create-post Modal. A Sheet's base
        // z-index sits a step below a Modal's, so at this nesting depth it would
        // only reach parity; lift it one step to sit clearly above the composer.
        zIndexOffset={Z_INDEX_STEP}
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[520px]"
        // Live autosave means there is nothing to lose, but a stray click while
        // typing shouldn't yank the editor away — only the X / Esc close it.
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        data-testid="altTextModal"
      >
        {/* Header */}
        <SheetHeader className="shrink-0 space-y-1 border-b border-neutral-100 px-6 py-4 text-left">
          <div className="flex items-center justify-between gap-3">
            <SheetTitle>Alt text</SheetTitle>
            <div className="flex items-center gap-3">
              <span
                className={cn(
                  "flex items-center gap-1 text-xs font-medium text-green-600 transition-opacity duration-200",
                  justSaved ? "opacity-100" : "opacity-0"
                )}
                aria-hidden={!justSaved}
              >
                <i className="fa-solid fa-circle-check" aria-hidden="true" />
                Saved
              </span>
              <button
                type="button"
                onClick={requestClose}
                aria-label="Close"
                className="rounded-sm text-neutral-500 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                <i className="fa-solid fa-xmark text-base" aria-hidden="true" />
              </button>
            </div>
          </div>
          <SheetDescription>
            Describe your images for screen readers and SEO. Alt text applies to
            images only — supported on LinkedIn, X, Facebook and Pinterest, and
            in beta on Instagram.
          </SheetDescription>
        </SheetHeader>

        {/* Body */}
        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
          {/* Keywords / SEO */}
          <div className="rounded-lg border border-miami-200 bg-miami-200/10 p-4">
            <Label
              htmlFor="alt-text-keywords"
              className="flex items-center gap-1.5 text-neutral-800"
            >
              <i
                className="fa-solid fa-wand-magic-sparkles text-miami-600"
                aria-hidden="true"
              />
              Keywords / SEO
              <span className="font-normal text-neutral-400">(optional)</span>
            </Label>
            <Input
              id="alt-text-keywords"
              value={keywords}
              onChange={(event) => setKeywords(event.target.value)}
              placeholder="e.g. vegan bakery, sourdough, Melbourne"
              maxLength={200}
              className="mt-2"
              aria-describedby="alt-text-keywords-help"
            />
            <p
              id="alt-text-keywords-help"
              className="mt-1.5 text-xs text-neutral-500"
            >
              Guides generation — we won't keyword-stuff or invent anything the
              image doesn't show.
            </p>
          </div>

          {hasImages && (
            <p className="text-xs font-medium uppercase tracking-[0.08em] text-neutral-500">
              {imageMediaFiles.length === 1
                ? "Image"
                : `Images · ${imageMediaFiles.length}`}
            </p>
          )}

          <div className="space-y-3">
            {imageMediaFiles.map((media, index) => {
              const key = keyOf(media);
              const status = statusByKey[key] || "idle";
              const isGenerating = status === "generating";
              const isError = status === "error" || errorKeys[key];
              const value = media.altText || "";
              const overLimit = value.length > altLimit.limit;
              const counterId = `alt-text-counter-${index}`;

              return (
                <div
                  key={key}
                  className={cn(
                    "flex items-start gap-4 rounded-lg border p-3 transition-colors",
                    isError
                      ? "border-red-100 bg-red-25"
                      : isGenerating
                        ? "border-miami-200 ring-1 ring-miami-200"
                        : "border-neutral-100"
                  )}
                  data-testid="altTextRow"
                >
                  <img
                    src={media.thumbnailUrl}
                    alt=""
                    className="h-16 w-16 shrink-0 rounded-md object-cover ring-1 ring-neutral-100"
                  />

                  <div className="min-w-0 flex-1">
                    {isGenerating ? (
                      <div
                        className="space-y-2 py-1"
                        data-testid="altTextSkeleton"
                      >
                        <div className="h-3 w-full rounded bg-gradient-to-r from-neutral-100 via-neutral-50 to-neutral-100 bg-[length:200%_100%] animate-shimmer motion-reduce:animate-none" />
                        <div className="h-3 w-2/3 rounded bg-gradient-to-r from-neutral-100 via-neutral-50 to-neutral-100 bg-[length:200%_100%] animate-shimmer motion-reduce:animate-none" />
                      </div>
                    ) : (
                      <Textarea
                        aria-label={`Alt text for image ${index + 1} of ${imageMediaFiles.length}`}
                        value={value}
                        placeholder="Write alt text, or tap the sparkle to generate"
                        onChange={(event) =>
                          handleUserEdit(media, event.target.value)
                        }
                        // Soft platform limits drive the counter; this hard cap
                        // only guards against pathological pastes.
                        maxLength={HARD_ALT_CEILING}
                        aria-describedby={counterId}
                        className="min-h-[60px] resize-none motion-safe:animate-in motion-safe:fade-in-0"
                      />
                    )}

                    <div className="mt-1.5 flex items-center justify-between gap-2 text-xs">
                      <span className="flex items-center gap-1">
                        {isGenerating && (
                          <span className="text-miami-600">
                            <i
                              className="fa-solid fa-wand-magic-sparkles"
                              aria-hidden="true"
                            />{" "}
                            Writing alt text…
                          </span>
                        )}
                        {isError && (
                          <span className="text-red-600">
                            <i
                              className="fa-solid fa-triangle-exclamation"
                              aria-hidden="true"
                            />{" "}
                            Couldn't generate.{" "}
                            <button
                              type="button"
                              onClick={() => handleRegenerateOne(media)}
                              className="font-medium text-blue-600 underline-offset-2 hover:underline"
                            >
                              Retry
                            </button>
                          </span>
                        )}
                        {status === "done" && !isError && (
                          <span className="flex items-center gap-1 text-green-600 motion-safe:animate-in motion-safe:zoom-in-50">
                            <i
                              className="fa-solid fa-circle-check"
                              aria-hidden="true"
                            />{" "}
                            Done
                          </span>
                        )}
                      </span>
                      <span
                        id={counterId}
                        className={cn(
                          "shrink-0 tabular-nums",
                          overLimit
                            ? "font-medium text-red-500"
                            : "text-neutral-400"
                        )}
                        title={
                          altLimit.platformLabel
                            ? `Limited by ${altLimit.platformLabel} (${altLimit.limit})`
                            : undefined
                        }
                      >
                        {value.length} / {altLimit.limit}
                      </span>
                    </div>
                    {overLimit && altLimit.platformLabel && (
                      <p className="mt-1 text-xs text-red-500">
                        Over {altLimit.platformLabel}'s {altLimit.limit}
                        -character limit.
                      </p>
                    )}
                  </div>

                  <div className="flex w-8 shrink-0 justify-center pt-1.5">
                    {isGenerating ? (
                      <Spinner size="sm" className="border-t-miami-400" />
                    ) : (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRegenerateOne(media)}
                        aria-label={`Generate alt text for image ${index + 1}`}
                        className="h-8 w-8 text-miami-600 hover:bg-miami-200/20 hover:text-miami-600"
                      >
                        <i
                          className="fa-solid fa-wand-magic-sparkles"
                          aria-hidden="true"
                        />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <SheetFooter className="shrink-0 flex-row items-center justify-between gap-2 border-t border-neutral-100 px-6 py-4">
          <Button
            type="button"
            variant="link"
            onClick={handleClearAll}
            className="px-0 text-neutral-500 hover:text-neutral-700"
          >
            Clear all
          </Button>
          <Button
            type="button"
            variant="blue-secondary"
            onClick={handleRegenerateAll}
            disabled={!hasImages || isAnyGenerating}
          >
            {isAnyGenerating ? (
              <Spinner size="sm" />
            ) : (
              <i
                className="fa-solid fa-wand-magic-sparkles"
                aria-hidden="true"
              />
            )}
            Regenerate all
          </Button>
        </SheetFooter>

        {/* Screen-reader-only status announcements */}
        <div aria-live="polite" className="sr-only">
          {liveMessage}
        </div>
        <div aria-live="assertive" className="sr-only">
          {errorMessage}
        </div>
      </SheetContent>

      <AlertDialog open={confirmRegenAll} onOpenChange={setConfirmRegenAll}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate all alt text?</AlertDialogTitle>
            <AlertDialogDescription>
              This will overwrite alt text you've edited by hand with fresh
              AI-generated suggestions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmRegenAll(false);
                doRegenerateAll();
              }}
            >
              Regenerate all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}

export const AltTextModal = AltTextModalComponent;
